import React, {FC, useEffect, useState} from 'react'
import {observer} from 'mobx-react'
import useStore from 'store'
import { FeatureIds } from 'utils/types'
import {useTranslation} from 'react-i18next'
import {Space} from '../../ui'
import { Switch } from '@material-ui/core'
import DeleteModuleButton from './DeleteModuleButton'
import {
    ModalRow,
    ModalText,
    ConfirmButtonSm,
    ButtonGroup,
    DialogHeaderText,
    ToggleGroup,
    ToggleRow,
    UrlInputWrapper,
    InputContainer,
    UrlInput,
    EmptyState
} from './styles'


export interface ProtocolFormState {
    isEnable: boolean
    link: string
}

interface ProtocolSectionProps {
    currentOrgId: number | undefined
    resetForm: boolean
    protocolModule?: any
}


const ProtocolSection: FC<ProtocolSectionProps> = ({ currentOrgId, resetForm, protocolModule }) => {
    const {modulesStore, orgs: orgsStore} = useStore()
    const {t} = useTranslation('communitySettings')

    const [isFormChanged, setIsFormChanged] = useState<boolean>(false)
    const [module, setModule] = useState<any>(null)
    // Baseline link to diff against; updated after a successful save so the
    // button disables even though the parent's protocolModule prop is stale.
    const [savedLink, setSavedLink] = useState<string>('')
    const [formState, setFormState] = useState<ProtocolFormState>({
        isEnable: false,
        link: ''
    })

    // The link/url lives on the protocols *module* (external_url), which is
    // separate from the enabled *feature*. It is only editable when the module
    // already exists (it carries the module_id the update endpoint needs).
    const originalLink = protocolModule?.external_url ?? ''
    const canEditLink = Boolean(protocolModule?.module_id)

    // Nothing here can be configured until the module has been added to the
    // community from the "add new module" dialog.
    const moduleAdded = Boolean(protocolModule?.module_id)

    useEffect(() => {
        const protocolFeature = modulesStore.communityFeatures.find(el => el.featureName === 'Protocols')
        if (protocolFeature) {
            setModule(protocolFeature)
            setFormState({ isEnable: protocolFeature.enabled, link: originalLink })
        } else {
            orgsStore.updateCommunity(currentOrgId, [...modulesStore.communityFeatures, {
                communityId: currentOrgId,
                enabled: false,
                featureName: 'Protocols',
                id: FeatureIds.PROTOCOLS,
                status: 2,
            }]).then(async () => {
                await modulesStore.getCommunityModulesAndFeatures(currentOrgId)
                const newFeature = modulesStore.communityFeatures.find(el => el.featureName === 'Protocols')
                if (newFeature) {
                    setModule(newFeature)
                    setFormState({ isEnable: newFeature.enabled, link: originalLink })
                }
            })
        }
    }, [currentOrgId])

    // Keep the link field in sync once the module prop finishes loading.
    useEffect(() => {
        setSavedLink(originalLink)
        setFormState(prev => ({ ...prev, link: originalLink }))
    }, [originalLink])

    useEffect(() => {
        if (resetForm) onResetForm()
    }, [resetForm])

    useEffect(() => {
        setIsFormChanged(
            formState.isEnable !== (module?.enabled ?? false) ||
            formState.link !== savedLink
        )
    }, [formState, module, savedLink])

    const Save = async () => {
        if (currentOrgId) {
            await orgsStore.updateCommunity(currentOrgId,
                modulesStore.communityFeatures.map(el => el.featureName === 'Protocols' ? {
                    communityId: el.communityId,
                    enabled: formState.isEnable,
                    featureName: el.featureName,
                    id: el.id,
                    status: formState.isEnable ? 1 : 2,
                } : el),)

            // Persist the link/url on the module itself if it changed.
            if (canEditLink && formState.link !== savedLink) {
                await modulesStore.updateModule({ ...protocolModule, external_url: formState.link })
            }

            await modulesStore.getCommunityModulesAndFeatures(currentOrgId)

            const protocolFeature = modulesStore.communityFeatures.find(el => el.featureName === 'Protocols')
            if (protocolFeature) {
                setModule(protocolFeature)
            }
            const refreshedModule = modulesStore.communityModules.find(el => el.module_id === protocolModule?.module_id)
            const newLink = refreshedModule?.external_url ?? formState.link
            setSavedLink(newLink)
            setFormState({
                isEnable: protocolFeature ? protocolFeature.enabled : formState.isEnable,
                link: newLink
            })
        }
    }

    const onResetForm = () => setFormState({
        isEnable: false,
        link: savedLink
    })

    const ToggleEnable = (value: any) => setFormState({...formState, isEnable: value })

    return (
        <>
            <ModalRow>
               <DialogHeaderText>{t`protocolsTab`}</DialogHeaderText>
               {moduleAdded &&
                 <ButtonGroup>
                   <DeleteModuleButton
                       module={protocolModule}
                       moduleLabel={t`protocolsTab`}
                       currentOrgId={currentOrgId}
                       onDeleted={onResetForm}
                   />
                   <ConfirmButtonSm disabled={!isFormChanged || module == null} onClick={Save} transparent>{t`save`}</ConfirmButtonSm>
                 </ButtonGroup>
               }
            </ModalRow>

            {!moduleAdded &&
                <EmptyState>
                    {t('moduleNotAdded', { module: t`protocolsTab` })}
                </EmptyState>
            }

            {moduleAdded && module &&
                <ToggleGroup>
                    <ToggleRow>
                        <ModalText>{t`isEnable`}:</ModalText>
                        <Switch
                            color="primary"
                            value={formState.isEnable}
                            onChange={({target}) => {
                                ToggleEnable(target.checked)
                            }}
                            checked={formState.isEnable}
                        />
                    </ToggleRow>
                </ToggleGroup>
            }

            {canEditLink &&
                <UrlInputWrapper>
                    <ModalText>{t`url`}:</ModalText>
                    <Space/>
                    <InputContainer>
                        <UrlInput
                            multiline
                            rows={2}
                            placeholder={t`url`}
                            value={formState.link}
                            onChange={({target}) => {
                                setFormState({...formState, link: target.value})
                            }}
                        />
                    </InputContainer>
                </UrlInputWrapper>
            }

            <Space/>
            <Space/>
        </>
    )
}

export default observer(ProtocolSection)
